import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import useNRLazyQuery from '../../../../graphql/useNRLazyQuery';
import useNRMutation from '../../../../graphql/UseNRMutation';
import {
  GET_JIRA_INTEGRATION,
  GET_JIRA_STATUS_MAPPING_PROBLEMS,
  GET_VULNERABILITY_JIRA_TICKETS,
  QL_JIRA_INTEGRATION_RECONNECT
} from '../../../../graphql/queries/jiraIntegration';
import JiraIcon from '../../../../NRIcons/IntegrationIcons/JiraIcon';
import { FiltersContext } from '../../../../pages/NRReportTabs/NRReportFilterContextWrapper';
import { RBAC_PERMISSIONS } from '../../../../pages/NRSettings/NRUsersAndAccess/NRRoles/NRCustomRoles/NRCustomRolePermissions.constants';
import { ACR_RESOURCE_TYPE } from '../../../../pages/NRSettings/NRUsersAndAccess/rbac.constants';
import { CONSTANTS } from '../../../../constants/constants';
import {
  getJiraInstanceHostFromTicketUrl,
  getJiraStatusMappingProblemsForTickets
} from '../../../../pages/NRSettings/NRIntegrations/NRJiraIntegration/jiraStatusMappingProblems.utils';
import WithPermissions from '../../../Permissions/WithPermissions';
import EllipsisTypography from '../../../EllipsisTypography/EllipsisTypography';
import NRLink from '../../../NRLink/NRLink';
import NRLoader from '../../../NRLoader/NRLoader';
import {
  JIRA_INTEGRATION_STATUS,
  JIRA_INTEGRATION_STATUS_LABELS
} from '../../../../pages/NRSettings/NRIntegrations/NRJiraIntegration/jiraIntegration.constants';
import {
  JIRA_CONNECTION_HEALTH_LABELS,
  JIRA_SYSTEM_CHECK_STATUS
} from '../../../../pages/NRSettings/NRIntegrations/NRJiraIntegration/jiraIntegrationDetail.constants';
import NRJiraReconnectModalContainer from '../../../../pages/NRSettings/NRIntegrations/NRJiraIntegration/NRJiraReconnectModalContainer';
import NRJiraSystemCheckIcon from '../../../../pages/NRSettings/NRIntegrations/NRJiraIntegration/NRJiraSystemCheckIcon';
import useCanManageJiraIntegration from '../../../../pages/NRSettings/NRIntegrations/NRJiraIntegration/useCanManageJiraIntegration';
import { CREATE_JIRA_TICKET_LABELS } from './createJiraTicket.constants';
import NRCreateVulnerabilityJiraTicket from './NRCreateVulnerabilityJiraTicket';
import NRJiraDisconnectedTicketsAlert from './NRJiraDisconnectedTicketsAlert';
import NRJiraTicketStatusMappingProblemAlert from './NRJiraTicketStatusMappingProblemAlert';
import NRJiraTicketsTable from './NRJiraTicketsTable';
import { VULNERABILITY_JIRA_TICKETS_LABELS, VULNERABILITY_JIRA_TICKETS_POLL_INTERVAL_MS } from './vulnerabilityJiraTickets.constants';

function NRActiveVulnerabilityJiraTickets({
  advisoryId,
  assetId,
  vulnerabilityData,
  cveDetails,
  sx,
  isDisconnected = false,
  canDirectReconnect = false
}) {
  const theme = useTheme();
  const { drawerContent, setDrawerContent, drawerTitle, setDrawerTitle, setHasBack, setOpenDrawer } = useContext(FiltersContext);
  const canManageJiraIntegration = useCanManageJiraIntegration();
  const [isReconnectModalOpen, setIsReconnectModalOpen] = useState(false);
  const [isReconnectPending, setIsReconnectPending] = useState(false);
  // null = still waiting for the tickets response. Avoids flashing "No ticket yet" while
  // useNRLazyQuery clears loading before its local result is synced.
  const [resolvedTickets, setResolvedTickets] = useState(null);

  const ticketArgs = useMemo(
    () => ({
      assetId,
      advisoryId,
      componentId: vulnerabilityData.componentId
    }),
    [advisoryId, assetId, vulnerabilityData.componentId]
  );

  const applyResolvedTickets = useCallback(list => {
    const next = Array.isArray(list) ? list : [];
    setResolvedTickets(prev => {
      if (prev === null) {
        return next;
      }
      if (
        prev.length === next.length &&
        prev.every(
          (ticket, index) =>
            ticket?.ticketId === next[index]?.ticketId &&
            ticket?.status === next[index]?.status &&
            ticket?.assignee === next[index]?.assignee &&
            ticket?.assigneeAvatarUrl === next[index]?.assigneeAvatarUrl &&
            ticket?.issueType === next[index]?.issueType &&
            ticket?.issueTypeIconUrl === next[index]?.issueTypeIconUrl &&
            ticket?.updatedAt === next[index]?.updatedAt &&
            ticket?.url === next[index]?.url
        )
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  const [tickets, ticketsLoading, ticketsCalled, fetchTickets, , , startPolling, stopPolling] = useNRLazyQuery({
    query: GET_VULNERABILITY_JIRA_TICKETS,
    options: {
      fetchPolicy: 'network-only',
      onCompleted: data => {
        applyResolvedTickets(data?.vulnerabilityJiraTickets);
      },
      onError: () => {
        // useNRLazyQuery surfaces errors via handleGraphQLError; only exit loading state here.
        applyResolvedTickets([]);
      }
    },
    qlObjKey: 'vulnerabilityJiraTickets',
    defaultValue: []
  });

  const [problemSpaces, , , fetchStatusMappingProblems] = useNRLazyQuery({
    query: GET_JIRA_STATUS_MAPPING_PROBLEMS,
    qlObjKey: 'jiraStatusMappingProblems',
    defaultValue: []
  });

  const fetchTicketsRef = useRef(fetchTickets);
  fetchTicketsRef.current = fetchTickets;
  const startPollingRef = useRef(startPolling);
  startPollingRef.current = startPolling;
  const stopPollingRef = useRef(stopPolling);
  stopPollingRef.current = stopPolling;

  const [reconnectJira, , isReconnecting, reconnectError] = useNRMutation(
    QL_JIRA_INTEGRATION_RECONNECT,
    'jiraIntegrationReconnect',
    JIRA_CONNECTION_HEALTH_LABELS.RECONNECT_SUCCESS,
    JIRA_CONNECTION_HEALTH_LABELS.RECONNECT_ERROR,
    { refetchQueries: [{ query: GET_JIRA_INTEGRATION }] }
  );

  // BE rejects vulnerabilityJiraTickets without an enabled installation — skip while disconnected.
  useEffect(() => {
    if (isDisconnected) {
      stopPollingRef.current();
      setResolvedTickets(null);
      return undefined;
    }

    setResolvedTickets(null);
    fetchTicketsRef.current({
      variables: { args: ticketArgs }
    });
    return undefined;
  }, [isDisconnected, ticketArgs]);

  // refetchQueries / polling may update hook result without Apollo onCompleted.
  // useNRLazyQuery resets result to [] before its data sync effect runs — never
  // let that stale empty array replace tickets we already got from onCompleted.
  useEffect(() => {
    if (isDisconnected || ticketsLoading || !ticketsCalled) {
      return;
    }

    if (resolvedTickets === null) {
      if (tickets.length > 0) {
        applyResolvedTickets(tickets);
      }
      return;
    }

    if (tickets.length === 0 && resolvedTickets.length > 0) {
      return;
    }

    applyResolvedTickets(tickets);
  }, [applyResolvedTickets, isDisconnected, resolvedTickets, tickets, ticketsCalled, ticketsLoading]);

  useEffect(() => {
    if (isDisconnected || !tickets.length) {
      stopPollingRef.current();
      return undefined;
    }

    startPollingRef.current(VULNERABILITY_JIRA_TICKETS_POLL_INTERVAL_MS);
    return () => {
      stopPollingRef.current();
    };
  }, [isDisconnected, tickets.length]);

  useEffect(() => {
    if (!isDisconnected) {
      setIsReconnectPending(false);
    }
  }, [isDisconnected]);

  useEffect(() => {
    if (reconnectError && !isReconnecting) {
      setIsReconnectPending(false);
    }
  }, [reconnectError, isReconnecting]);

  useEffect(() => {
    if (isDisconnected || resolvedTickets === null || !resolvedTickets.length) {
      return;
    }

    fetchStatusMappingProblems();
  }, [fetchStatusMappingProblems, isDisconnected, resolvedTickets]);

  const ticketStatusMappingProblems = useMemo(() => {
    const ticketsList = resolvedTickets || [];
    const instanceUrl = getJiraInstanceHostFromTicketUrl(ticketsList.find(ticket => ticket.url)?.url);
    return getJiraStatusMappingProblemsForTickets(ticketsList, problemSpaces, instanceUrl);
  }, [problemSpaces, resolvedTickets]);

  const storedTicketCount = vulnerabilityData.jiraTicketCount || 0;
  const ticket = !isDisconnected && resolvedTickets?.length > 0 ? resolvedTickets[0] : null;
  const displayedTicketCount = isDisconnected ? storedTicketCount : resolvedTickets?.length || storedTicketCount;
  const hasExistingTicket = Boolean(ticket) || displayedTicketCount > 0;
  const canCreateTicket = !isDisconnected && !hasExistingTicket;
  const showTicketsLoading = !isDisconnected && resolvedTickets === null;
  const isReconnectInProgress = isReconnecting || isReconnectPending;

  const ticketsLoader = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: theme.spacing(3)
      }}
    >
      <NRLoader />
    </Box>
  );

  const handleCreateTicket = useCallback(
    event => {
      event?.preventDefault?.();

      if (!canCreateTicket) {
        return;
      }

      if (drawerContent.length) {
        setHasBack(true);
      }

      setDrawerTitle([...drawerTitle, CREATE_JIRA_TICKET_LABELS.DRAWER_TITLE]);
      setDrawerContent([
        ...drawerContent,
        <NRCreateVulnerabilityJiraTicket
          key="create-jira-ticket"
          vulnerabilityData={vulnerabilityData}
          cveDetails={cveDetails}
          assetId={assetId}
          advisoryId={advisoryId}
        />
      ]);
      setOpenDrawer(true);
    },
    [
      advisoryId,
      assetId,
      canCreateTicket,
      cveDetails,
      drawerContent,
      drawerTitle,
      setDrawerContent,
      setDrawerTitle,
      setHasBack,
      setOpenDrawer,
      vulnerabilityData
    ]
  );

  const handleReconnect = useCallback(
    event => {
      event?.preventDefault?.();
      event?.stopPropagation?.();

      if (isReconnecting || isReconnectPending) {
        return;
      }

      if (canDirectReconnect) {
        setIsReconnectPending(true);
        reconnectJira();
        return;
      }

      setIsReconnectModalOpen(true);
    },
    [canDirectReconnect, isReconnectPending, isReconnecting, reconnectJira]
  );

  const handleReconnectModalComplete = useCallback(() => {
    setIsReconnectModalOpen(false);
  }, []);

  const sectionSx = {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(2),
    overflow: 'hidden',
    mt: theme.spacing(2)
  };

  const createTicketButton = canCreateTicket && (
    <WithPermissions
      permission={CONSTANTS.PERMISSIONS.WRITE_IMGS}
      rbacPermission={RBAC_PERMISSIONS.JIRA_TICKETS_CREATE}
      resourceType={ACR_RESOURCE_TYPE.ASSET}
    >
      <Box sx={{ flexShrink: 0, ml: theme.spacing(2) }}>
        <Button variant="outlined" color="secondary" onClick={handleCreateTicket}>
          {VULNERABILITY_JIRA_TICKETS_LABELS.CREATE_TICKET}
        </Button>
      </Box>
    </WithPermissions>
  );

  const ticketHeaderContent = ticket ? (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        minWidth: 0
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
        <Avatar
          sx={{
            width: theme.spacing(6),
            height: theme.spacing(6),
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.iconInfo
          }}
        >
          <JiraIcon width={theme.spacing(4)} height={theme.spacing(4)} />
        </Avatar>
        <Box sx={{ ml: theme.spacing(1), minWidth: 0 }}>
          <NRLink
            url={ticket.url}
            text={ticket.ticketKey}
            tooltipTitle={VULNERABILITY_JIRA_TICKETS_LABELS.OPEN_TICKET}
            sx={{ width: 'auto' }}
          />
          <EllipsisTypography variant="body2" color="text.secondary" showTooltip>
            {ticket.spaceName}
          </EllipsisTypography>
        </Box>
      </Box>
    </Box>
  ) : (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        minWidth: 0
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
        <Avatar
          sx={{
            width: theme.spacing(6),
            height: theme.spacing(6),
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.iconInfo
          }}
        >
          <JiraIcon width={theme.spacing(4)} height={theme.spacing(4)} />
        </Avatar>
        <Box sx={{ ml: theme.spacing(1), minWidth: 0 }}>
          {isDisconnected ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1), minWidth: 0 }}>
              <Typography variant="h3" color="text.primary">
                {VULNERABILITY_JIRA_TICKETS_LABELS.SECTION_LABEL}
              </Typography>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: theme.spacing(0.5), flexShrink: 0 }}>
                <NRJiraSystemCheckIcon status={JIRA_SYSTEM_CHECK_STATUS.ERROR} size={20} />
                <Typography component="span" variant="body2" color="error.main">
                  {JIRA_INTEGRATION_STATUS_LABELS[JIRA_INTEGRATION_STATUS.NOT_CONNECTED]}
                </Typography>
              </Box>
            </Box>
          ) : (
            <>
              <Typography variant="label" color="text.secondary">
                {VULNERABILITY_JIRA_TICKETS_LABELS.SECTION_LABEL}
              </Typography>
              <Typography variant="h3" color="text.primary">
                {VULNERABILITY_JIRA_TICKETS_LABELS.NO_TICKETS}
              </Typography>
            </>
          )}
        </Box>
      </Box>
      {createTicketButton}
    </Box>
  );

  return (
    <Box sx={[sectionSx, sx]}>
      {showTicketsLoading ? (
        ticketsLoader
      ) : (
        <>
          <Box sx={{ p: theme.spacing(2) }}>{ticketHeaderContent}</Box>
          {isDisconnected && (
            <Box sx={{ px: theme.spacing(2), pb: theme.spacing(2) }}>
              <NRJiraDisconnectedTicketsAlert
                onReconnect={canManageJiraIntegration ? handleReconnect : undefined}
                isReconnecting={isReconnectInProgress}
              />
            </Box>
          )}
          {!isDisconnected && ticketStatusMappingProblems.length > 0 && (
            <Box sx={{ px: theme.spacing(2), pb: theme.spacing(2) }}>
              <NRJiraTicketStatusMappingProblemAlert problems={ticketStatusMappingProblems} />
            </Box>
          )}
          {Boolean(ticket) && <NRJiraTicketsTable tickets={[ticket]} />}
        </>
      )}
      {canManageJiraIntegration && (
        <NRJiraReconnectModalContainer
          open={isReconnectModalOpen}
          onClose={() => setIsReconnectModalOpen(false)}
          onComplete={handleReconnectModalComplete}
        />
      )}
    </Box>
  );
}

export default NRActiveVulnerabilityJiraTickets;
